import { useCallback, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Box, Button, DataTableColumnSettings, Icon, Text, useIsMobile } from "@salestrak/ui";
import { PageHeader } from "#app/components/page-header";
import {
  WorkflowsTable,
  useWorkflowColumns,
} from "#app/components/workflows/workflows-table";
import { CreateWorkflowDialog } from "#app/components/workflows/create-workflow-dialog";
import { DeleteWorkflowDialog } from "#app/components/workflows/delete-workflow-dialog";
import { ExpandableSearch } from "@salestrak/ui";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { WORKFLOW_LIST } from "#app/lib/filters/list-configs";
import type { WorkflowRow } from "#app/lib/data";
import { useWorkflowMutations } from "#app/lib/hooks/use-workflow-mutations";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { usePermission } from "#app/lib/hooks/use-permission";

/**
 * `/workflows` — the workflows roster: a plain `DataTable` of every workflow (a
 * single workflow's issues live at `/workflow/$workflowId`). Owns its own search
 * / sort / column-settings state, mirroring the teams + members rosters.
 */
function WorkflowsRoute() {
  const { push } = useAppNavigate();
  const isMobile = useIsMobile();
  const canCreate = usePermission("create.workflows");
  const { setDefaultWorkflow } = useWorkflowMutations();

  const { searchValue, setSearchValue, sorting, setSorting, where, orderBy } =
    useListQueryState(WORKFLOW_LIST);

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteWorkflow, setDeleteWorkflow] = useState<WorkflowRow | null>(null);

  const onEdit = useCallback(
    (workflowId: string) => push(`/settings/workflows/${workflowId}`),
    [push],
  );
  const onSetDefault = useCallback(
    (workflow: WorkflowRow) => {
      void setDefaultWorkflow(workflow.workflowId);
    },
    [setDefaultWorkflow],
  );

  const columns = useWorkflowColumns(onEdit);
  const tableView = useTableViewSettings("workflows:app");

  return (
    <Box fill="flex">
      <PageHeader
        title="Workflows"
        actions={
          <>
            <ExpandableSearch
              onValueChange={setSearchValue}
              placeholder="Search workflows..."
              size="xs"
            />
            <DataTableColumnSettings
              columns={columns}
              pinnedColumnIds={["name"]}
              {...tableView.settingsProps}
            />
            {canCreate ? (
              <Button
                size={isMobile ? "icon-xs" : "xs"}
                onClick={() => setCreateOpen(true)}
              >
                {isMobile ? <Icon name="plus" /> : <Text>New workflow</Text>}
              </Button>
            ) : null}
          </>
        }
      />
      <WorkflowsTable
        where={where}
        orderBy={orderBy}
        sorting={sorting}
        onSortingChange={setSorting}
        searchValue={searchValue}
        columns={columns}
        tableViewProps={tableView.tableProps}
        onEdit={onEdit}
        onSetDefault={onSetDefault}
        onRequestDelete={setDeleteWorkflow}
      />
      <CreateWorkflowDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(workflowId) => push(`/settings/workflows/${workflowId}`)}
      />
      <DeleteWorkflowDialog
        workflow={deleteWorkflow}
        open={!!deleteWorkflow}
        onOpenChange={(open) => {
          if (!open) setDeleteWorkflow(null);
        }}
        onManageWorkflow={onEdit}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/workflows")({
  component: WorkflowsRoute,
});
